import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const SettingsContext = createContext(null);

const FALLBACK = {
  theme: { accent: "#DB960D", accentDark: "#B67B08", background: "#F4F4F4" },
  hero: {
    badge: "Smart Home Accessories",
    heading: "Perfecting smart homes",
    subtext: "Precision 3D-printed mounts and stands, designed to disappear into your interior.",
    image: "",
  },
  brand: { name: "KANN", tagline: "Precision 3D-printed accessories for the smart home.", logo: "" },
  cta: { heading: "Ready to tidy up?", subtext: "Give your smart home the finishing touch it deserves." },
  footer: {
    tagline:
      "Precision 3D-printed accessories for the smart home. Designed to feel intentional, discreet, and right at home — because technology shouldn't stand out, it should fit in.",
  },
  shipping: { be: 8.59, eu: 19.97, vat_rate: 21 },
  seasonal: {
    enabled: false,
    theme: "summer",
    start_date: "",
    end_date: "",
    banner_enabled: true,
    banner_text: "☀️ Summer at KANN — brighten up your smart home",
    banner_link: "/shop",
  },
  seo: {},
  maintenance: {
    enabled: false,
    title: "Even in onderhoud",
    message: "We voeren wat werk uit aan de webshop. We zijn zo weer online.",
  },
};

// A warm, sunny palette applied on top of the base theme when the summer season is active.
export const SUMMER_THEME = { accent: "#F7941E", accentDark: "#D97706", background: "#FFFAF3" };

// Option C: enabled is the master switch; if dates are set, the theme is only
// active within [start_date, end_date]. Empty dates = no bound on that side.
export function isSeasonalActive(seasonal) {
  if (!seasonal?.enabled) return false;
  const d = new Date(); // local date (avoids UTC off-by-one near midnight)
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (seasonal.start_date && today < seasonal.start_date) return false;
  if (seasonal.end_date && today > seasonal.end_date) return false;
  return true;
}

function applyTheme(theme, active) {
  const root = document.documentElement;
  if (theme?.accent) root.style.setProperty("--gold", theme.accent);
  if (theme?.accentDark) root.style.setProperty("--gold-dark", theme.accentDark);
  if (theme?.background) root.style.setProperty("--site-bg", theme.background);
  root.setAttribute("data-season", active ? "summer" : "");
}

function merge(data) {
  return {
    theme: { ...FALLBACK.theme, ...(data.theme || {}) },
    hero: { ...FALLBACK.hero, ...(data.hero || {}) },
    brand: { ...FALLBACK.brand, ...(data.brand || {}) },
    cta: { ...FALLBACK.cta, ...(data.cta || {}) },
    footer: { ...FALLBACK.footer, ...(data.footer || {}) },
    shipping: { ...FALLBACK.shipping, ...(data.shipping || {}) },
    seasonal: { ...FALLBACK.seasonal, ...(data.seasonal || {}) },
    seo: { ...FALLBACK.seo, ...(data.seo || {}) },
    maintenance: { ...FALLBACK.maintenance, ...(data.maintenance || {}) },
  };
}

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(FALLBACK);

  const apply = (merged) => {
    const active = isSeasonalActive(merged.seasonal);
    applyTheme(active ? SUMMER_THEME : merged.theme, active);
    const s = merged.seasonal || {};
    const bannerVisible = active && !!s.banner_enabled && !!(s.banner_text || "").trim();
    document.documentElement.style.setProperty("--season-banner-h", bannerVisible ? "40px" : "0px");
    setSettings(merged);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      apply(merge(data));
    } catch {
      applyTheme(FALLBACK.theme, false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveSettings = async (next) => {
    const { data } = await api.put("/settings", next);
    const merged = merge(data);
    apply(merged);
    return merged;
  };

  const seasonalActive = isSeasonalActive(settings.seasonal);

  return (
    <SettingsContext.Provider value={{ settings, saveSettings, reload: load, seasonalActive }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
